/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  StyleSheet,
} from 'react-native';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { ParamListBase } from '@react-navigation/native';
import { applyStoreReviewReply, fetchStoreReviewById } from '../infra/storeReviewsRepo';
import {
  STORE_REVIEW_REPLY_MAX,
  type StoreReviewRow,
} from '../domain/storeReviewTypes';
import { ticketColors } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { showError, showSuccess } from '../../shared/ui/appToast';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { spacing } from '../../shared/theme/spacing';

function starsLabel(score: number): string {
  return `${'★'.repeat(Math.max(0, Math.min(5, score)))}${'☆'.repeat(Math.max(0, 5 - score))}`;
}

function formatDate(iso: string): string {
  return formatLocalDateTime(iso);
}

export function StoreReviewDetailScreen() {
  const route = useRoute<RouteProp<ParamListBase, 'StoreReviewDetail'>>();
  const reviewId = (route.params as { reviewId: string }).reviewId;
  const canReply = useModulePermission('BranchSettings', 'reply');
  const { layoutWide } = useContentLayout();
  const [review, setReview] = useState<StoreReviewRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const firstHydrateRef = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchStoreReviewById(reviewId);
      setReview(data);
      if (firstHydrateRef.current) {
        setReply(data?.merchant_reply ?? '');
        firstHydrateRef.current = false;
      } else if (data) {
        if (reply === review?.merchant_reply) {
          setReply(data.merchant_reply ?? '');
        }
      }
    } catch (e) {
      console.error('StoreReviewDetailScreen load', e);
      setError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);

  useEffect(() => {
    firstHydrateRef.current = true;
    load();
  }, [load]);

  const handlePublish = async () => {
    if (!canReply) {
      showError('Sin permiso', 'No podés responder reseñas.');
      return;
    }
    const trimmed = reply.trim();
    if (!trimmed) {
      showError('Error', 'Escribí una respuesta.');
      return;
    }
    if (trimmed.length > STORE_REVIEW_REPLY_MAX) {
      showError('Error', `Máximo ${STORE_REVIEW_REPLY_MAX} caracteres.`);
      return;
    }
    setSubmitting(true);
    try {
      await applyStoreReviewReply(reviewId, trimmed);
    } catch (e) {
      showError('Error', e instanceof Error ? e.message : 'No se pudo publicar.');
      setSubmitting(false);
      return;
    }
    await load();
    setSubmitting(false);
    showSuccess('Listo', 'Ya está publicada.');
  };

  const containerStyle = layoutWide
    ? [styles.container, styles.containerWide]
    : [styles.container, styles.containerCompact];

  if (loading && !review) {
    return (
      <ShellContentFrame style={containerStyle}>
        <ModuleEmptyState
          loading
          loadingText="Cargando…"
          testID="store-reviews.detail.loading"
        />
      </ShellContentFrame>
    );
  }

  if (error && !review) {
    return (
      <ShellContentFrame style={containerStyle}>
        <ModuleEmptyState
          variant="nest"
          kicker="Reseña"
          title="No se pudo cargar la reseña"
          subtitle="Revisá tu conexión y probá de nuevo."
          primaryAction={{ label: 'Reintentar', onPress: () => void load() }}
        />
      </ShellContentFrame>
    );
  }

  if (!review) {
    return (
      <ShellContentFrame style={containerStyle}>
        <ModuleEmptyState
          variant="nest"
          kicker="Reseña"
          title="No encontramos esa reseña"
          subtitle="Puede que se haya borrado o que el link no sea válido."
        />
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={containerStyle}>
      <TicketModuleHeader
        kicker="Reseña"
        title={starsLabel(review.score)}
        meta={formatDate(review.created_at)}
        testID="store-reviews.detail.header"
      />

      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl
            refreshing={loading}
            onRefresh={() => void load()}
            colors={[ticketColors.ink2]}
            tintColor={ticketColors.ink2}
          />
        }
      >
        <PaperSheet padded style={styles.reviewSheet}>
          <Text style={styles.comment}>
            {review.comment || 'El cliente no dejó comentario.'}
          </Text>
          <Text style={styles.customerLabel} testID="store-reviews.detail.customer-label">
            {review.customer_id ? 'Cliente verificado' : 'Cliente anónimo'}
          </Text>
          {review.order_id ? (
            <Text style={styles.orderLabel}>
              Pedido: {review.order_id.slice(0, 8)}
            </Text>
          ) : null}
          {review.appointment_id ? (
            <Text style={styles.orderLabel}>
              Turno: {review.appointment_id.slice(0, 8)}
            </Text>
          ) : null}
          {review.merchant_replied_at ? (
            <Text style={styles.repliedAt}>
              Respondida: {formatDate(review.merchant_replied_at)}
            </Text>
          ) : null}
        </PaperSheet>

        <PaperSheet padded style={styles.replySheet}>
          <Text style={styles.sectionTitle} testID="store-reviews.detail.section.reply">
            Respuesta del local
          </Text>
          <Text style={styles.hint}>La ve el cliente. Hasta {STORE_REVIEW_REPLY_MAX} caracteres.</Text>
          <TextInput
            style={styles.input}
            value={reply}
            onChangeText={(t) => setReply(t.slice(0, STORE_REVIEW_REPLY_MAX))}
            placeholder="Gracias por tu comentario…"
            placeholderTextColor={ticketColors.ink3}
            multiline
            textAlignVertical="top"
            editable={canReply && !submitting}
            maxLength={STORE_REVIEW_REPLY_MAX}
            accessibilityLabel="Respuesta del local"
            accessibilityHint={`Escribí tu respuesta. Máximo ${STORE_REVIEW_REPLY_MAX} caracteres.`}
            testID="store-reviews.detail.reply-input"
          />
          <Text style={styles.counter} testID="store-reviews.detail.counter">
            {reply.trim().length}/{STORE_REVIEW_REPLY_MAX}
          </Text>

          {canReply ? (
            <TouchableOpacity
              style={[styles.btn, submitting && styles.btnDisabled]}
              onPress={handlePublish}
              disabled={submitting}
              accessibilityRole="button"
              accessibilityLabel={
                submitting ? 'Publicando…' : review.merchant_reply ? 'Actualizar respuesta' : 'Publicar respuesta'
              }
              testID="store-reviews.detail.publish"
            >
              <Text style={styles.btnText}>
                {submitting ? 'Publicando…' : review.merchant_reply ? 'Actualizar respuesta' : 'Publicar respuesta'}
              </Text>
            </TouchableOpacity>
          ) : (
            <Text style={styles.noPermission} testID="store-reviews.detail.no-permission">
              No podés responder reseñas.
            </Text>
          )}
        </PaperSheet>
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: {
    ...ticketSurfaces.screen,
  },
  containerWide: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  containerCompact: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  scroll: {
    gap: spacing.md,
    paddingBottom: spacing.xxl,
  },
  reviewSheet: {
    gap: spacing.xs,
  },
  replySheet: {
    gap: spacing.xs,
  },
  comment: {
    fontSize: 15,
    color: ticketColors.ink,
    lineHeight: 22,
  },
  customerLabel: {
    fontSize: 12,
    color: ticketColors.ink2,
    marginTop: spacing.xs,
  },
  orderLabel: {
    fontSize: 12,
    color: ticketColors.ink2,
  },
  repliedAt: {
    fontSize: 12,
    color: ticketColors.ink2,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  hint: {
    fontSize: 12,
    color: ticketColors.ink2,
    marginBottom: spacing.xs,
  },
  input: {
    minHeight: 120,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: spacing.md,
    fontSize: 15,
    color: ticketColors.ink,
    backgroundColor: ticketColors.paper2,
  },
  counter: {
    fontSize: 11,
    color: ticketColors.ink2,
    textAlign: 'right',
    marginTop: spacing.xs,
  },
  btn: {
    marginTop: spacing.md,
    height: 48,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnDisabled: {
    opacity: 0.6,
  },
  btnText: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.goldInk,
  },
  noPermission: {
    fontSize: 13,
    color: ticketColors.ink2,
    marginTop: spacing.md,
    textAlign: 'center',
  },
});
